/**
 * Homelya — "Adicionar ao carrinho"
 *
 * 1) <add-to-cart-only>: adiciona o produto ao carrinho SEM redirecionar
 *    (o botão "Comprar agora" continua levando ao /cart -> checkout Yampi)
 *    e atualiza o contador do ícone do carrinho no cabeçalho.
 *      - Carrinho em modo "página": atualiza só o ícone/contador.
 *      - Carrinho em modo "pop-up" ou "notificação": atualiza itens e contador pelo
 *        fluxo padrão do tema (renderContents). O pop-up NÃO abre sozinho.
 *      - Em ambos, mostra o ícone flutuante do carrinho por 8 segundos (ver abaixo).
 *
 * 2) window.homelyaYampiCheckout(): leva o carrinho ao checkout da Yampi.
 *    Usada pelo "Finalizar Carrinho" do pop-up (#CartDrawer-Checkout) e pelo
 *    "Comprar agora" da página do produto (ver product-form.js).
 */

if (!customElements.get('add-to-cart-only')) {
  customElements.define(
    'add-to-cart-only',
    class AddToCartOnly extends HTMLElement {
      constructor() {
        super();
        this.onClick = this.onClick.bind(this);
        this.syncDisabled = this.syncDisabled.bind(this);
        this.resetTimer = null;
      }

      connectedCallback() {
        this.button = this.querySelector('[data-add-to-cart-only]');
        this.productForm = this.closest('product-form');
        this.form = this.productForm?.querySelector('form');
        this.label = this.button?.querySelector('.product-form__add-only-label');
        if (!this.button || !this.form || !this.label) return;

        this.defaultLabel = this.button.dataset.labelDefault || this.label.textContent.trim();
        this.addedLabel = this.button.dataset.labelAdded || this.defaultLabel;

        this.button.addEventListener('click', this.onClick);

        // Espelha o estado (disponível / esgotado) do botão principal, que o tema
        // já atualiza quando o cliente troca a variante.
        this.mainButton = this.productForm.querySelector('[type="submit"]');
        if (this.mainButton) {
          this.observer = new MutationObserver(this.syncDisabled);
          this.observer.observe(this.mainButton, { attributes: true, attributeFilter: ['disabled'] });
          this.syncDisabled();
        }
      }

      disconnectedCallback() {
        this.observer?.disconnect();
        this.button?.removeEventListener('click', this.onClick);
        clearTimeout(this.resetTimer);
      }

      syncDisabled() {
        this.button.toggleAttribute('disabled', this.mainButton.hasAttribute('disabled'));
      }

      setLoading(isLoading) {
        this.button.classList.toggle('loading', isLoading);
        this.button.setAttribute('aria-disabled', isLoading ? 'true' : 'false');
        this.querySelector('.loading__spinner')?.classList.toggle('hidden', !isLoading);
      }

      showAdded() {
        clearTimeout(this.resetTimer);
        this.label.textContent = this.addedLabel;
        this.button.classList.add('is-added');
        this.resetTimer = setTimeout(() => {
          this.label.textContent = this.defaultLabel;
          this.button.classList.remove('is-added');
        }, 2200);
      }

      async onClick(evt) {
        evt.preventDefault();
        if (this.button.disabled || this.button.getAttribute('aria-disabled') === 'true') return;
        // Evita corrida com um clique em "Comprar agora" que já esteja em andamento.
        if (this.mainButton?.getAttribute('aria-disabled') === 'true') return;

        this.setLoading(true);
        this.productForm.handleErrorMessage?.();

        const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        const sections = cart ? cart.getSectionsToRender().map((section) => section.id) : ['cart-icon-bubble'];
        formData.append('sections', sections.join(','));
        if (cart) {
          formData.append('sections_url', window.location.pathname);
          cart.setActiveElement?.(document.activeElement);
        }
        config.body = formData;

        const variantId = formData.get('id');

        try {
          const response = await fetch(`${routes.cart_add_url}`, config).then((r) => r.json());

          if (response.status) {
            if (typeof publish === 'function') {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: variantId,
                errors: response.errors || response.description,
                message: response.message,
              });
            }
            this.productForm.handleErrorMessage?.(response.description || response.message);
            return;
          }

          if (cart) {
            // Pop-up / notificação: mesmo fluxo do botão "Comprar agora" do tema.
            if (typeof publish === 'function') {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: variantId,
                cartData: response,
              });
            }
            cart.renderContents(response); // atualiza itens e contador (não abre o pop-up)
            if (cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
          } else {
            await AddToCartOnly.updateCartIcon(response.sections?.['cart-icon-bubble']);
          }
          window.homelyaShowFloatingCart?.();

          this.showAdded();
        } catch (e) {
          console.error(e);
          this.productForm.handleErrorMessage?.(window.cartStrings?.error);
        } finally {
          this.setLoading(false);
        }
      }

      // Atualiza o ícone do carrinho (e o contador) no cabeçalho.
      static async updateCartIcon(sectionHtml) {
        const target = document.getElementById('cart-icon-bubble');
        if (!target) return;

        if (!sectionHtml) {
          const url = `${window.location.pathname}?sections=cart-icon-bubble`;
          const data = await fetch(url, { headers: { Accept: 'application/json' } })
            .then((r) => (r.ok ? r.json() : null))
            .catch(() => null);
          sectionHtml = data?.['cart-icon-bubble'];
        }
        if (!sectionHtml) return;

        const doc = new DOMParser().parseFromString(sectionHtml, 'text/html');
        const source = doc.querySelector('.shopify-section') || doc.body;
        target.innerHTML = source.innerHTML;

        const bubble = target.querySelector('.cart-count-bubble');
        if (bubble) {
          bubble.classList.remove('is-bumping');
          void bubble.offsetWidth; // reinicia a animação
          bubble.classList.add('is-bumping');
        }
      }
    }
  );
}

/**
 * Envia o carrinho atual para a Yampi e redireciona ao checkout dela.
 * Usada por: "Comprar agora" (produto) e "Finalizar Carrinho" (pop-up).
 * Se a integração da Yampi não estiver ativa, segue para o checkout padrão.
 */
window.homelyaYampiCheckout = function () {
  const fallback = () => {
    if (typeof hideYampiLoader === 'function') hideYampiLoader();
    window.location.href = '/checkout';
  };

  // Sem a integração da Yampi na página, usa o checkout padrão.
  if (typeof window.cartEndpoint === 'undefined') return fallback();

  if (typeof showYampiLoader === 'function') showYampiLoader();

  return fetch('/cart.json', { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
    .then((r) => r.json())
    .then((cartPayload) =>
      fetch(window.cartEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ shop: window.location.host, cart_payload: cartPayload }),
      }).then((r) => r.json())
    )
    .then((resp) => {
      if (resp && resp.checkout_direct_url) {
        window.location.href =
          typeof ymp_getUrlWithUtms === 'function' ? ymp_getUrlWithUtms(resp.checkout_direct_url) : resp.checkout_direct_url;
      } else {
        fallback();
      }
    })
    .catch(fallback);
};

/* Pop-up e página do carrinho: "Finalizar Carrinho" -> checkout da Yampi */
document.addEventListener('click', (evt) => {
  const checkoutButton = evt.target.closest?.('#CartDrawer-Checkout, #checkout.cart__checkout-button');
  if (!checkoutButton || checkoutButton.disabled) return;
  evt.preventDefault();
  window.homelyaYampiCheckout();
});

/**
 * Ícone flutuante do carrinho
 * Aparece fixo na lateral da tela por 8 segundos, com um leve "pulsar", sempre que um
 * produto é adicionado (e reinicia a contagem se outro produto for adicionado).
 * Clicar nele abre o pop-up do carrinho.
 */
(function () {
  const VISIBLE_MS = 35000;
  let el = null;
  let inner = null;
  let hideTimer = null;
  let lastCount = 0;

  const readCount = () =>
    parseInt(document.querySelector('#cart-icon-bubble .cart-count-bubble [aria-hidden="true"]')?.textContent, 10) || 0;

  function ensureElement() {
    if (el) return el;
    el = document.createElement('a');
    el.className = 'homelya-floating-cart';
    el.href = (window.routes && window.routes.cart_url) || '/cart';
    el.setAttribute('role', 'button');
    inner = document.createElement('span');
    inner.className = 'homelya-floating-cart__inner';
    el.appendChild(inner);

    el.addEventListener('click', (evt) => {
      const drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.open === 'function') {
        evt.preventDefault();
        clearTimeout(hideTimer);
        el.classList.remove('is-visible');
        drawer.open(el);
      }
    });

    document.body.appendChild(el);
    return el;
  }

  window.homelyaShowFloatingCart = function () {
    const headerIcon = document.getElementById('cart-icon-bubble');
    if (!headerIcon) return;

    ensureElement();
    // Usa o próprio ícone do carrinho do tema (com o contador atualizado).
    inner.innerHTML = headerIcon.innerHTML;
    lastCount = readCount();

    // Reinicia a entrada e o "pulsar" a cada produto adicionado.
    el.classList.remove('is-visible');
    void el.offsetWidth;
    el.classList.add('is-visible');

    clearTimeout(hideTimer);
    hideTimer = setTimeout(() => el.classList.remove('is-visible'), VISIBLE_MS);
  };

  document.addEventListener('DOMContentLoaded', () => {
    lastCount = readCount();

    // Adição rápida pelas coleções: mostra o ícone só quando a quantidade de itens aumenta.
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (event?.source !== 'quick-add') return;
        const count = event.cartData?.item_count;
        if (typeof count === 'number' && count > lastCount) window.homelyaShowFloatingCart();
        else if (typeof count === 'number') lastCount = count;
      });
    }
  });
})();
